/**
 * Shared primitives for every DTO (§15.1 conventions): ids are ULID strings, instants are UTC ISO strings,
 * mutations carry an Idempotency-Key, list endpoints are cursor-paginated.
 */
import { z } from 'zod';
/** Internal ids are ULIDs (docs/VOCABULARY.md → Identifiers). */
export const Ulid = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'expected a ULID');
/** UTC instant in ISO-8601 with an explicit offset (storage and DTOs are UTC; display uses the lottery timezone). */
export const IsoDateTime = z.iso.datetime({ offset: true });
/** Lowercase URL slug; the tenant is resolved from it server-side, never from a body. */
export const Slug = z
    .string()
    .min(3)
    .max(64)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'lowercase letters, digits and hyphens');
/** BCP-47 language tag, e.g. `en`, `en-NG`, `fr`, `ar` (RTL is a presentation concern, §15.4). */
export const LocaleTag = z.string().regex(/^[a-z]{2,3}(?:-[A-Za-z]{2,4})?(?:-[A-Z]{2}|-\d{3})?$/);
const isSupportedTimezone = (tz) => {
    try {
        new Intl.DateTimeFormat('en', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
};
/** IANA timezone name (D01). Validated against the runtime's ICU data. */
export const IanaTimezone = z.string().min(1).refine(isSupportedTimezone, 'unknown IANA timezone');
/** Lower-case hex SHA-256 digest. */
export const HexHash256 = z.string().regex(/^[0-9a-f]{64}$/, 'expected 64 hex characters');
/** 32 raw bytes as 64 hex characters (seeds). */
export const HexBytes32 = HexHash256;
/** ISO-3166-1 alpha-2 country code. */
export const CountryCode = z.string().regex(/^[A-Z]{2}$/);
/** sRGB hex colour `#rrggbb` (theme tokens, prize accents). */
export const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'expected #rrggbb');
export const HttpsUrl = z.url({ protocol: /^https?$/ });
/** E.164 phone number (§15.4). */
export const E164Phone = z.string().regex(/^\+[1-9]\d{6,14}$/, 'expected an E.164 number');
/** Social handle: platform-specific validation happens in the engagement module; this is the transport shape. */
export const SocialHandle = z
    .string()
    .min(2)
    .max(64)
    .regex(/^@?[A-Za-z0-9._-]+$/);
/** Non-negative integer transported as a JSON number (counts, positions, versions — never money). */
export const NonNegativeInt = z.number().int().min(0);
export const PositiveInt = z.number().int().min(1);
/** §15.1: every mutation accepts an `Idempotency-Key` header. Parsed from the lower-cased header map. */
export const IdempotencyKey = z
    .string()
    .min(8)
    .max(128)
    .regex(/^[A-Za-z0-9._:-]+$/);
export const IdempotencyKeyHeader = z.object({ 'idempotency-key': IdempotencyKey });
export const IDEMPOTENCY_KEY_HEADER = 'idempotency-key';
/** Cursor pagination for list endpoints. */
export const PaginationQuery = z.object({
    cursor: z.string().max(256).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const PageInfo = z.object({
    nextCursor: z.string().nullable(),
    limit: z.number().int(),
    /** Total is optional: counting is expensive on large tables and never required by the UI. */
    total: z.number().int().optional(),
});
/** Factory for a paginated response envelope around any item schema. */
export const paginated = (item) => z.object({ items: z.array(item), page: PageInfo });
/** Sort direction used by board/list queries. */
export const SortDirection = z.enum(['asc', 'desc']);
/** A masked contact as admins see it by default (§16.3): never the raw value. */
export const MaskedValue = z.string().max(64);
/** Reference to a stored object (evidence, export, share image) — a key, never a public URL with PII. */
export const StorageRef = z.string().min(1).max(512);
/** Localized string map keyed by locale tag; the first configured locale is the fallback. */
export const LocalizedText = z.record(LocaleTag, z.string());
//# sourceMappingURL=common.js.map